/**
 * Glisser-déposer un fichier : ce qu'une zone de dépôt retient de ce qu'on lui a lâché.
 *
 * Règle de Benjamin (2026-10-12) : partout où l'on peut importer un document, on peut AUSSI le
 * déposer d'un glisser. Ce module est la partie qui se teste ; `composants/ZoneDepot.tsx` ne fait que
 * l'afficher. Le contrôle réel du fichier (octets, type, taille) reste côté serveur, comme pour le
 * sélecteur de fichiers : celui d'ici n'évite qu'un aller-retour inutile et dit pourquoi on refuse.
 */

export interface FichiersRetenus {
  /** Les fichiers à traiter (jamais plus d'un quand la zone n'en accepte qu'un). */
  readonly fichiers: readonly File[];
  /** Pourquoi quelque chose a été écarté, ou `null` : à afficher dans la zone, jamais ignoré en silence. */
  readonly refus: string | null;
}

/** Le survol porte-t-il des FICHIERS (et non du texte sélectionné ou une adresse glissés par erreur) ? */
export function porteDesFichiers(types: readonly string[]): boolean {
  return types.includes('Files');
}

/**
 * Un fichier correspond-il à l'attribut `accept` d'un champ fichier (`.xlsx`, `text/csv`, `image/*`) ?
 * Sans `accept`, tout passe. Les jetons se comparent sans tenir compte de la casse ; une extension se
 * lit sur le nom, un type MIME sur `file.type` (souvent vide sous Windows pour les formats peu courants :
 * c'est pourquoi l'extension, quand elle est donnée, suffit).
 */
export function fichierAccepte(fichier: { readonly name: string; readonly type: string }, accept?: string): boolean {
  if (!accept) return true;
  const jetons = accept
    .split(',')
    .map((j) => j.trim().toLowerCase())
    .filter((j) => j !== '');
  if (jetons.length === 0) return true;
  const nom = fichier.name.toLowerCase();
  const type = fichier.type.toLowerCase();
  return jetons.some((j) => {
    if (j.startsWith('.')) return nom.endsWith(j);
    if (j.endsWith('/*')) return type.startsWith(j.slice(0, -1));
    return type === j;
  });
}

/** Les formats attendus, en mots simples : `.xlsx` → « xlsx », `application/pdf` → « pdf », `image/*` → « images ». */
export function formatsLisibles(accept?: string): string {
  if (!accept) return '';
  const noms = accept
    .split(',')
    .map((j) => j.trim().toLowerCase())
    .filter((j) => j !== '')
    .map((j) => {
      if (j.startsWith('.')) return j.slice(1);
      if (j.endsWith('/*')) return j === 'image/*' ? 'images' : j.slice(0, -2);
      return j.split('/')[1] ?? j;
    });
  return [...new Set(noms)].join(', ');
}

/**
 * Ce que l'on garde d'un dépôt. Un dépôt sans fichier, trop de fichiers pour une zone qui n'en prend
 * qu'un, ou un format non accepté : rien n'est traité, et la raison est donnée. Pour plusieurs fichiers,
 * ceux qui sont acceptés sont gardés et les autres sont nommés dans le refus.
 */
export function retenirFichiers(
  deposes: readonly File[],
  options: { readonly multiple?: boolean; readonly accept?: string } = {},
): FichiersRetenus {
  if (deposes.length === 0) {
    return { fichiers: [], refus: 'Aucun fichier reçu : déposez un fichier depuis l’explorateur.' };
  }
  if (!options.multiple && deposes.length > 1) {
    return { fichiers: [], refus: 'Un seul fichier à la fois : déposez-en un.' };
  }

  const acceptes = deposes.filter((f) => fichierAccepte(f, options.accept));
  const ecartes = deposes.filter((f) => !fichierAccepte(f, options.accept));
  if (ecartes.length === 0) return { fichiers: acceptes, refus: null };

  const formats = formatsLisibles(options.accept);
  const nommes = ecartes.map((f) => `« ${f.name} »`).join(', ');
  const attendu = formats === '' ? '' : ` Formats acceptés : ${formats}.`;
  return {
    fichiers: acceptes,
    refus:
      ecartes.length === 1
        ? `${nommes} n’a pas un format accepté ici.${attendu}`
        : `${ecartes.length} fichiers écartés (format non accepté) : ${nommes}.${attendu}`,
  };
}
